// Route guard requiring `system_admin` role. Wraps the /admin/* route
// group. Customers and agents who hit this URL get a 403-style page
// rather than a redirect, so it's obvious they don't have permission
// (the URL is real, they just can't see it).
//
// Mirror in spirit of Auware's AdminRoute guard.
import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuthStore } from "@/stores/auth";

export function AdminRoute(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-neutral-50">
        <svg
          className="h-8 w-8 animate-spin text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      </div>
    );
  }

  if (!user) {
    const redirectTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectTo)}`} replace />;
  }

  if (!user.isSystemAdmin) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-neutral-50 p-8 text-center">
        <i
          className="fa-duotone fa-thin fa-shield-halved text-5xl text-neutral-400"
          aria-hidden="true"
        />
        <h1 className="text-2xl font-semibold text-neutral-900">
          Access denied
        </h1>
        <p className="max-w-md text-sm text-neutral-600">
          This area is restricted to system administrators. If you think
          you should have access, contact a Buyist Support admin.
        </p>
      </div>
    );
  }

  return <Outlet />;
}

export default AdminRoute;
